import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, ClipboardPaste, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

export const abs = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("blob:") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

// Read the first image from the system clipboard (Paste button). Ctrl/Cmd+V is handled by the page.
export async function readClipboardImage() {
  if (!navigator.clipboard?.read) throw new Error("Your browser can't read the clipboard here — press Ctrl+V / ⌘V instead.");
  let items;
  try { items = await navigator.clipboard.read(); }
  catch { throw new Error("Clipboard access was blocked — press Ctrl+V / ⌘V on this page to paste instead."); }
  for (const it of items) {
    const type = it.types.find((t) => t.startsWith("image/"));
    if (type) { const b = await it.getType(type); return new File([b], `pasted.${type.split("/")[1] || "png"}`, { type }); }
  }
  throw new Error("No image found on the clipboard.");
}

export async function uploadThumbImage(file, role) {
  if (!file?.type?.startsWith("image/")) throw new Error("Please choose an image file.");
  return api.ytUpload(file, role);
}

// One image input with upload, paste-from-clipboard, preview and remove.
export function ImageSlot({ label, hint, value, onChange, role, canPaste, active, onFocus, testid, tall }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const take = async (file) => {
    setBusy(true);
    try { onChange(await uploadThumbImage(file, role)); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message || "Upload failed"); }
    setBusy(false);
  };
  const paste = async (e) => {
    e.stopPropagation();
    try { await take(await readClipboardImage()); } catch (err) { toast.error(err.message); }
  };
  return (
    <div onClick={onFocus} data-testid={testid}
      className={`group relative overflow-hidden rounded-xl border bg-white/[0.02] transition-colors ${active ? "border-[#FF3B3B]/70" : "border-white/10 hover:border-white/25"}`}>
      {value ? (
        <>
          <img src={abs(value.url)} alt="" className={`w-full object-cover ${tall ? "h-40" : "h-24"}`} />
          <button onClick={(e) => { e.stopPropagation(); onChange(null); }} data-testid={`${testid}-remove`}
            className="absolute right-1.5 top-1.5 rounded-full bg-black/70 p-1 text-white hover:bg-[#FF3B3B]"><X size={12} /></button>
        </>
      ) : (
        <button onClick={() => ref.current?.click()} data-testid={`${testid}-upload`}
          className={`flex w-full flex-col items-center justify-center gap-1 text-center ${tall ? "h-40" : "h-24"}`}>
          {busy ? <Loader2 size={18} className="animate-spin text-[#FF3B3B]" /> : <ImagePlus size={18} className="text-white/40 group-hover:text-white/70" />}
          <span className="text-[11px] font-bold text-white/70">{label}</span>
          {hint && <span className="px-2 text-[10px] text-white/35">{hint}</span>}
        </button>
      )}
      {canPaste && !value && (
        <button onClick={paste} data-testid={`${testid}-paste`} title="Paste image from clipboard"
          className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white/70 hover:text-white">
          <ClipboardPaste size={11} /> Paste
        </button>
      )}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) take(f); }} />
    </div>
  );
}
